import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { DashboardStatRowAllView } from "@/components/dashboard/dashboard-stats/DashboardStatRowAllView";
import { DashboardStatRowIndividualView } from "@/components/dashboard/dashboard-stats/DashboardStatRowIndividualView";

import { ALL_USERS_KEY } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  DashboardComparisonMetric,
  DashboardKpiOverview,
  DashboardSelectedUser,
  GoalPeriod,
} from "@/types";

export const DashboardStatRow = ({
  goalsPeriod,
  kpiOverview,
  // Stays mounted while the header minimizes so the grid track can animate
  // closed; cards fade out and clip inside the shrinking row.
  isMinimized,
  isPending,
  onGoalsPeriodChange,
  selectedUserId,
}: {
  goalsPeriod: GoalPeriod;
  kpiOverview: DashboardKpiOverview | undefined;
  isMinimized: boolean;
  isPending: boolean;
  onGoalsPeriodChange: (period: GoalPeriod) => void;
  selectedUserId: DashboardSelectedUser;
}) => {
  /* React Hooks */
  const [comparisonMetric, setComparisonMetric] =
    useState<DashboardComparisonMetric>(
      DashboardComparisonMetric.TIME_TO_SCREEN,
    );
  const location = useLocation();

  // Every staffer switch and every fresh navigation reopens on time to screen.
  useEffect(() => {
    setComparisonMetric(DashboardComparisonMetric.TIME_TO_SCREEN);
  }, [location.key, selectedUserId]);

  /* Helpers */
  const itemClassName = cn(
    "min-h-0 min-w-0 transition-opacity duration-300 ease-in-out",
    isMinimized
      ? "pointer-events-none overflow-hidden opacity-0"
      : "overflow-visible",
  );

  /* Render */
  const render = () => {
    if (selectedUserId === ALL_USERS_KEY) {
      return (
        <DashboardStatRowAllView
          comparisonMetric={comparisonMetric}
          goalsPeriod={goalsPeriod}
          isPending={isPending}
          itemClassName={itemClassName}
          onGoalsPeriodChange={onGoalsPeriodChange}
          onMetricChange={setComparisonMetric}
          overview={kpiOverview}
        />
      );
    }

    return (
      <DashboardStatRowIndividualView
        comparisonMetric={comparisonMetric}
        goalsPeriod={goalsPeriod}
        isPending={isPending}
        itemClassName={itemClassName}
        onGoalsPeriodChange={onGoalsPeriodChange}
        onMetricChange={setComparisonMetric}
        overview={kpiOverview}
        selectedUserId={selectedUserId}
      />
    );
  };

  return render();
};
